import type { ReactNode } from 'react';
import { useRef } from 'react';

import { Badge } from 'design-system';

import { ChartProvider } from '@/features/charts/context/ChartContext';
import { CsvDownloadProvider } from '@/features/charts/context/CsvTableContext';
// oxlint-disable-next-line gx-boundaries/no-cross-feature-import -- Preserve the dashboard-owned animation wrapper used by market widget grid items.
import { ElectricBorder, useScrollToAnimatedElement } from '@/features/dashboard';
import { LightModeProvider } from '@/shared/lib/LightModeContext';

import type { MarketsComponentType } from '../types';
import { MarketAppControlsProvider } from './contexts/MarketAppControlsContext';
import { MarketWidgetProvider } from './contexts/MarketWidgetContext';
import TabsContainer from './TabsContainer';
import Tiles from './Tiles';

type ComponentProps = {
  components: MarketsComponentType[] | undefined;
  last?: ReactNode;
  showShareBlock?: boolean;
  editMode?: boolean;
  showAddToDashboard?: boolean;
  forChat?: boolean;
};
export const ComponentMapper = ({
  component,
  blockIndex
}: {
  component: MarketsComponentType;
  blockIndex: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isAnimating = useScrollToAnimatedElement(ref, component.id);

  if (component.component) {
    if (component.component.type === 'tiles') {
      return component.component.tiles.length ? (
        <Tiles
          key={component.id}
          tiles={component.component.tiles}
        />
      ) : null;
    }
  } else if (component.tabs?.length) {
    return (
      <div
        ref={ref}
        id={component.id}
        className={`h-full [&>*]:h-full${component.layout?.fullWidth ? ' lg:col-span-2' : ''}`}
      >
        <ElectricBorder animate={isAnimating}>
          <MarketWidgetProvider>
            <ChartProvider>
              <CsvDownloadProvider>
                <LightModeProvider>
                  <TabsContainer
                    key={component.id}
                    blockIndex={blockIndex}
                    layout={component.layout}
                    tabs={component.tabs.map(tab => {
                      if (tab.type === 'feed' || tab.type === 'monthly_recap')
                        return { ...tab, tag: <Badge variant='muted'>beta</Badge> };
                      return tab;
                    })}
                  />
                </LightModeProvider>
              </CsvDownloadProvider>
            </ChartProvider>
          </MarketWidgetProvider>
        </ElectricBorder>
      </div>
    );
  }

  return null;
};

/**
 * MarketsComponents - renders a collection of different market-related components
 */
const MarketsComponents = ({
  components,
  last,
  showShareBlock,
  editMode,
  showAddToDashboard = true,
  forChat = false
}: ComponentProps) => {
  return (
    <MarketAppControlsProvider
      editMode={editMode}
      showAddToDashboard={showAddToDashboard}
      showShareBlock={showShareBlock}
    >
      <div
        className={`apps__container grid w-full grid-cols-1 items-stretch gap-6 [&>*]:min-w-0 [&>*]:self-stretch [&>*]:overflow-hidden${
          forChat ? '' : ' lg:grid-cols-2'
        }`}
        data-testid='market-widgets-grid'
      >
        {components?.map((component, index) => {
          return (
            <ComponentMapper
              key={component.id}
              blockIndex={index}
              component={component}
            />
          );
        })}
        {last}
      </div>
    </MarketAppControlsProvider>
  );
};

export default MarketsComponents;
